/**
 * ArchLang Validation (v0.2)
 *
 * Post-resolution validation. Consumes a `ResolvedModel` and produces
 * diagnostics for v0.2 rules that go beyond what the resolver/parser
 * already check.
 *
 * Rules enforced here:
 *   - Process step shape: caller is module/actor; callee is interface leaf
 *   - Required blanks must be filled or dropped (not left as blanks in
 *     instances)
 *   - Reserved-keyword names rejected on declarations
 *   - Unknown kind references in process steps
 *
 * Rules enforced elsewhere:
 *   - One-op-per-target: in resolver (mergeBodies)
 *   - Override only on inherited entity: in resolver
 *   - Drop only on inherited entity: in resolver
 *   - cascade/append mutual exclusion: in parser
 *   - `type process` / `type view` rejection: in parser + resolver
 *   - Lex/parse errors: in lexer + parser
 */

import type {
  Body,
  DescriptionEntry,
  Diagnostic,
  FieldDecl,
  InterfaceDecl,
  AspectEntry,
  ParsedFile,
  ProcessDecl,
  ProcessStep,
  QualifiedName,
  SourceSpan,
  SubprocessDecl,
  SurfaceDecl,
  Value,
} from "./types.js";
import { processQualifiedName, subprocessQualifiedName } from "./types.js";
import type {
  ResolvedSurface,
  ResolvedInterface,
  ResolvedModel,
  ResolvedModule,
} from "./resolver.js";
import {
  buildSymbolSets,
  buildModuleCandidates,
  buildScopedModuleIndex,
  buildIfaceCandidates,
  sameSpace,
  type ScopedCand,
} from "./hoist-index.js";
import { spaceChain } from "./resolver.js";
import type { Flow, CallHop, CallNode, SpliceNode, ProcessNode, TerminatorNode } from "./process-model.js";
import { forEachCallHop, forEachNode, collectBindNames } from "./process-ir-walk.js";
import { parseDuration } from "@archlang/calc";
import { buildIndexes, buildGraph } from "./graph.js";
import { buildNodeUniverse } from "./selector-eval.js";
import { lifecycleReferenceDiagnostics } from "./lifecycle-diagnostics.js";
import {
  buildDescriptionIndex,
  aspectLookup,
  nodeDescriptionContext,
  makeDescriptionContext,
  renderDescription,
  renderMarkdownDocument,
  type DescriptionIssue,
} from "./description.js";
import type { DecorationInstance, DecorationRegistry } from "./decorations.js";
import { forEachAspectValue } from "./aspects.js";
import { findProcessRoleConflicts, unresolvedInclusions } from "./stubs.js";
import { resolvePresenceRef } from "./presence.js";
import { makeDiag } from "./diagnostics/catalog.js";
import { modelCarriesLifecycle, validateDecorations } from "./decoration-validate.js";

const RESERVED_NAMES: ReadonlySet<string> = new Set([
  "type",
  "in",
  "aspects",
  "required",
  "cascade",
  "append",
  "override",
  "drop",
  "process",
  "view",
  "alt",
  "par",
  "focus",
  "group",
  "by",
  "layout",
  "include",
  "exclude",
  "true",
  "false",
]);

export interface ValidationResult {
  readonly diagnostics: readonly Diagnostic[];
}

export function validate(model: ResolvedModel): ValidationResult {
  const diagnostics: Diagnostic[] = [];

  validateNames(model, diagnostics);
  diagnostics.push(...unnamedModuleDiagnostics(model.modules));
  validateRequiredBlanks(model, diagnostics);
  validateDecorations(model, diagnostics);
  const symbolIndex = buildSymbolIndex(model);
  const roleConflicts = findProcessRoleConflicts(model);
  for (const proc of model.processes) {
    validateProcess(proc, symbolIndex, model, diagnostics, roleConflicts);
  }
  // Validate each subprocess body once. Type-declared subprocesses are stamped
  // onto every instance (same source span, different ownerPath) — dedupe by
  // source so a shared subprocess isn't reported once per instance.
  const seenSub = new Set<string>();
  for (const sub of model.subprocesses) {
    const key = sub.source
      ? `${sub.source.file}:${sub.source.start.offset}`
      : `name:${sub.name}`;
    if (seenSub.has(key)) continue;
    seenSub.add(key);
    validateSubprocess(sub, symbolIndex, model, diagnostics, roleConflicts);
  }
  validateProcessOwners([...(model.processModels ?? []), ...(model.subprocessModels ?? [])], diagnostics);
  diagnostics.push(
    ...descriptionDiagnosticsScoped(
      model.modules,
      model.types,
      buildDescriptionIndex(model, model.documents ?? []),
      model.spaces
    )
  );
  documentDiagnostics(model, diagnostics);
  validateWidgetTags(model, diagnostics);
  validateAspectRefs(model, symbolIndex, diagnostics);
  validatePlanePresence(model, diagnostics);
  validateAspectPlacement(model, diagnostics);
  validatePartialPresence(model, diagnostics);
  validateInclusions(model, diagnostics);
  validateLatencyFields(model, diagnostics);
  validateLatencyCoverage(model, diagnostics);
  validateLifecycleReferences(model, diagnostics);

  return { diagnostics };
}

/**
 * Scrub-relative reference findings (§9.1): a live element calling something
 * retired, deprecated, or not built yet.
 *
 * Gated on the model carrying a lifecycle decoration at all, because the check
 * needs a dependency graph and a node universe — real work to build, and wasted
 * on the overwhelming majority of models, which carry no lifecycle at all. The
 * gate is also exactly what makes "no lifecycle ⇒ no findings" true by
 * construction rather than by the walk happening to find nothing.
 */
export function validateLifecycleReferences(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  if (!modelCarriesLifecycle(model)) return;
  diagnostics.push(
    ...lifecycleReferenceDiagnostics(buildGraph(model), buildNodeUniverse(model), {
      registry: model.decorationRegistry,
      // Without this the walk judges every reference at `EMPTY_TIMELINE.now`
      // (-0.5) — a point before every real ordinal, so nothing is ever
      // deprecated or retired and the whole check silently answers "clean".
      timeline: model.timeline,
    }),
  );
}

/**
 * v0.10.2 latency analysis: a `latency` field (on an interface, or a note step's
 * body) is interpreted as a duration. An authored value that fails its own
 * convention — off the time family (`2w` = writes), a bare number, garbage, or
 * a list (`50ms, 500ms`, a mis-spelled range) — is a **warning**. A missing
 * value is not (absence is not draft debt here). Only malformed *authored*
 * values warn; the estimation fold treats every gap as analysis data (§Time
 * analysis), never a diagnostic.
 */
export function validateLatencyFields(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  const check = (fields: readonly FieldDecl[]): void => {
    const f = fields.find((x) => x.path.length === 1 && x.path[0] === "latency");
    if (!f || !f.value) return;
    if (f.value.kind === "list") {
      diagnostics.push(
        makeDiag(
          "warning",
          f.source,
          "LATENCY_MALFORMED",
          "validation.latencyIsList"
        )
      );
      return;
    }
    const raw = latencyScalar(f.value);
    if (raw === undefined) return;
    const parsed = parseDuration(raw);
    if (!parsed.ok && parsed.reason !== "empty") {
      diagnostics.push(
        makeDiag(
          "warning",
          f.source,
          "LATENCY_MALFORMED",
          "validation.latencyBadValue",
          { raw }
        )
      );
    }
  };
  walkInterfaces(model.modules, (i) => check(i.body.fields));
  for (const pm of [...(model.processModels ?? []), ...(model.subprocessModels ?? [])]) {
    forEachNode(pm.body, (node) => {
      if (node.kind === "note" && node.fields) check(node.fields);
    });
  }
}

function latencyScalar(v: Value): string | undefined {
  if (v.kind === "identifier" || v.kind === "string") return v.text;
  if (v.kind === "number") return String(v.value); // a bare number has no unit → malformed
  return undefined;
}

/** A resolved interface's `latency`: priced (valid), missing (absent/empty), or
 *  malformed (present but unparseable). Malformed is owned by LATENCY_MALFORMED,
 *  so coverage must NOT also flag it "has no latency". */
function ifaceLatencyState(iface: ResolvedInterface): "priced" | "missing" | "malformed" {
  const f = iface.body.fields.find((x) => x.path.length === 1 && x.path[0] === "latency");
  if (!f?.value) return "missing";
  const raw = latencyScalar(f.value);
  if (raw === undefined) return "missing";
  return parseDuration(raw).ok ? "priced" : "malformed";
}

/** Stdlib async/streaming interface roots — they model no request/response
 *  latency, so they are exempt from timing coverage (arch.backend messaging +
 *  the streaming protocol variants). A subtype inherits async-ness via its chain. */
const ASYNC_KIND_ROOTS: ReadonlySet<string> = new Set([
  "event", "kafka", "amqp", "nats", "mqtt", "redis_pubsub", "sse", "webhook", "websocket",
  "grpc_server_stream", "grpc_client_stream", "grpc_bidi_stream", "graphql_subscription",
]);

/** True when the interface's kind (or an ancestor) is an async/streaming root. */
function ifaceIsAsync(iface: ResolvedInterface, model: ResolvedModel): boolean {
  const chain = [iface.typeName, ...(model.registry.kinds.get(iface.typeName)?.chain ?? [])];
  return chain.some((name) => ASYNC_KIND_ROOTS.has(name));
}

/**
 * v0.10.2 partial-model validity: opting out of latency (a process with **no**
 * timed calls) is fine, but a **partly** timed process — some sync callees have
 * a `latency`, some don't — leaves the gaps as **TODO** (draft debt to fill in
 * so the estimate completes). Async callees are exempt. Deduped per interface
 * (an interface unpriced across several partial processes flags once).
 */
export function validateLatencyCoverage(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  const index = buildIndexes(model).interfacesByPath;
  const flagged = new Set<string>();
  // Both processes AND subprocesses can be partly-timed — a subprocess's own
  // callees are coverage subjects too (matches validateLatencyFields's scope).
  const bodies: Flow[] = [
    ...(model.processModels ?? []).map((p) => p.body),
    ...(model.subprocessModels ?? []).map((s) => s.body),
  ].filter((b): b is Flow => !!b);
  for (const body of bodies) {
    const sync: ResolvedInterface[] = [];
    forEachCallHop(body, (hop) => {
      if (hop.callee.anonymous) return;
      const iface = index.get(hop.callee.interfacePath.join("."));
      if (iface && !ifaceIsAsync(iface, model)) sync.push(iface);
    });
    // Malformed callees are excluded from the partition — LATENCY_MALFORMED
    // already owns them; flagging "has no latency" would be a wrong second diag.
    const priced = sync.filter((i) => ifaceLatencyState(i) === "priced");
    const unpriced = sync.filter((i) => ifaceLatencyState(i) === "missing");
    if (priced.length === 0 || unpriced.length === 0) continue; // untimed or fully timed → fine
    for (const iface of unpriced) {
      const key = iface.decl.source ? `${iface.decl.source.file}:${iface.decl.source.start.offset}` : iface.name;
      if (flagged.has(key)) continue;
      flagged.add(key);
      diagnostics.push(
        makeDiag(
          "todo",
          iface.decl.nameSource ?? iface.decl.source,
          "LATENCY_INCOMPLETE",
          "validation.latencyIncomplete",
          { name: iface.name }
        )
      );
    }
  }
}

/**
 * v0.9.0 §1.2: a reference inside a body `on p { … }` block must name a member
 * of the main body — a presence block PROJECTS existing members, so an
 * unresolved one is an error, not the auto-stub a top-level inclusion gets.
 *
 * §7 also puts both surfaces in ONE namespace: a plane-exclusive member must
 * not collide with a main-body one. Every family loses data quietly when it
 * happens — a field merges by path with last-write-wins, and a SUBMODULE merges
 * by name into one node that keeps the block's born-on plane, so the main-body
 * copy (and everything under it) drops off primary. Two interfaces of one name
 * do raise a DUPLICATE_INTERFACE of their own, but that is a warning about a
 * repeated declaration; it does not say the main-body one just left primary.
 */
export function validatePartialPresence(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  const walk = (m: ResolvedModule): void => {
    const blocks = m.decl.body.partialOn ?? [];
    // The decl body is POST-fold — the resolver appended each block's members to
    // the main body — so a name written in both places appears TWICE. That
    // duplicate is the collision signal. Counted across every family in one map
    // because the namespace is shared: an interface and a field named alike
    // collide just as surely as two fields, and a type-named surface
    // (`api Public`) is filed under `modules` until the resolver reclassifies it.
    const nameCount = new Map<string, number>();
    const bump = (name: string): void => {
      // An anonymous bare-step process (§4.13) has no name; two of them share
      // the empty key, and a namespace clash needs a NAME to clash on.
      if (name === "") return;
      nameCount.set(name, (nameCount.get(name) ?? 0) + 1);
    };
    for (const f of m.decl.body.fields) bump(f.path.join("."));
    for (const c of m.decl.body.modules) bump(c.name);
    for (const p of m.decl.body.processes) bump(p.name);
    for (const i of m.decl.body.interfaces) bump(i.name);
    for (const s of m.decl.body.surfaces) bump(s.name);
    // What the BLOCKS contribute to those counts, and on how many distinct
    // planes. The rest is what the main body holds — the copy a collision
    // destroys. Two claims on ONE plane are an ordinary duplicate (same
    // presence set, DUPLICATE_* owns it); a claim facing a main-body copy, or
    // two claims on DIFFERENT planes, is the §7 clash.
    const claims = new Map<string, { count: number; planes: Set<string> }>();
    const claim = (name: string, plane: string): void => {
      if (name === "") return;
      const at = claims.get(name) ?? { count: 0, planes: new Set<string>() };
      at.count += 1;
      at.planes.add(plane);
      claims.set(name, at);
    };
    for (const pp of blocks) {
      const plane = pp.plane.join(".");
      for (const f of pp.body.fields) claim(f.path.join("."), plane);
      for (const i of pp.body.interfaces) claim(i.name, plane);
      for (const s of pp.body.surfaces) claim(s.name, plane);
      for (const c of pp.body.modules) claim(c.name, plane);
      for (const p of pp.body.processes) claim(p.name, plane);
    }
    // A dropped name has no main-body copy left to lose — the author removed it
    // on purpose, so the block's is the only one and nothing is destroyed.
    const dropped = new Set(m.decl.body.drops.map((d) => d.target.join(".")));
    const reported = new Set<string>();
    const collides = (name: string): boolean => {
      if (name === "" || reported.has(name) || dropped.has(name)) return false;
      const at = claims.get(name);
      if (!at) return false;
      const inMainBody = (nameCount.get(name) ?? 0) - at.count;
      return inMainBody > 0 || at.planes.size > 1;
    };
    for (const pp of blocks) {
      const plane = pp.plane.join(".");
      for (const ref of pp.refs ?? []) {
        if (resolvePresenceRef(m, ref.path)) continue;
        diagnostics.push(
          makeDiag(
            "error",
            ref.source ?? pp.source,
            "PRESENCE_REF_UNRESOLVED",
            "validation.presenceRefUnresolved",
            { ref: ref.path.join("."), owner: m.name, onClause: `on ${pp.plane.join(".")} { … }` }
          )
        );
      }
      // Every family the block can declare: colliding with the main body (or
      // with another block) loses one of the two without a word.
      const claimed: { name: string; source?: SourceSpan }[] = [
        ...pp.body.fields.map((f) => ({ name: f.path.join("."), source: f.source })),
        ...pp.body.interfaces.map((i) => ({ name: i.name, source: i.source })),
        ...pp.body.surfaces.map((s) => ({ name: s.name, source: s.source })),
        ...pp.body.modules.map((c) => ({ name: c.name, source: c.source })),
        ...pp.body.processes.map((p) => ({ name: p.name, source: p.source })),
      ];
      for (const c of claimed) {
        if (!collides(c.name)) continue;
        reported.add(c.name);
        diagnostics.push(
          makeDiag(
            "error",
            c.source ?? pp.source,
            "PRESENCE_MEMBER_COLLISION",
            "validation.presenceMemberCollision",
            { member: c.name, owner: m.name, onClause: `on ${plane} { … }` }
          )
        );
      }
    }
    for (const c of m.modules) walk(c);
  };
  for (const m of model.modules) walk(m);
}

/**
 * v0.9.0 §1.2: an inclusion reference inside a top-level `on <plane> { … }`
 * block must name a module declared elsewhere. An unresolved target is a `TODO`
 * (§4.12) — reported here, and stubbed dashed by `synthesizeStubs`, which shares
 * this pass's `unresolvedInclusions` so diagnostic and stub always agree.
 */
export function validateInclusions(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  for (const inc of unresolvedInclusions(model)) {
    diagnostics.push(
      makeDiag(
        "todo",
        inc.source,
        "INCLUSION_UNRESOLVED",
        "validation.inclusionUnresolved",
        { target: inc.target.join(".") }
      )
    );
  }
}

/**
 * v0.9.0 §1.4 — aspect/roster placement hardening:
 *   - identical duplicate instances in one array (`Euro as active, Euro as
 *     active`) → error (`DUPLICATE_INSTANCE`);
 *   - one instance name bound to two DIFFERENT members at a place → warning
 *     (`INSTANCE_NAME_COLLISION`), the node-local name is ambiguous;
 *   - a member-side aspect and a place-side roster naming the SAME membership
 *     (member+place+key) with DIFFERENT instances → warning
 *     (`ROSTER_MEMBER_CONFLICT`);
 *   - `on` presence inside a SURFACE body is ignored → warning
 *     (`SURFACE_ON_IGNORED`).
 */
export function validateAspectPlacement(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  // A written ref → module path: exact qualified match, else the unique
  // last-segment candidate (mirrors presence.ts `resolveRef`). Ambiguity → no
  // match (its own diagnostic owns it).
  const known = new Set<string>();
  const byLast = new Map<string, string[]>();
  const indexMod = (m: ResolvedModule, parent: readonly string[]): void => {
    const path = [...parent, m.name];
    const key = path.join(".");
    known.add(key);
    (byLast.get(m.name) ?? byLast.set(m.name, []).get(m.name)!).push(key);
    for (const c of m.modules) indexMod(c, path);
  };
  for (const m of model.modules) indexMod(m, []);
  const resolveRef = (segs: readonly string[]): string | undefined => {
    const q = segs.join(".");
    if (known.has(q)) return q;
    const cands = byLast.get(segs[segs.length - 1] ?? "");
    if (!cands) return undefined;
    if (cands.length === 1) return cands[0];
    const suffix = cands.filter((c) => c === q || c.endsWith(`.${q}`));
    return suffix.length === 1 ? suffix[0] : undefined;
  };

  // Atomic ref items of an aspect value, WITH the span to anchor a diagnostic.
  const refItems = (v: Value | undefined): { path: readonly string[]; instance?: string; span?: SourceSpan }[] => {
    if (!v) return [];
    if (v.kind === "identifier") return [{ path: [v.text], span: v.source }];
    if (v.kind === "ref") return [{ path: v.path, instance: v.instance, span: v.instanceSource ?? v.source }];
    if (v.kind === "list") return v.items.flatMap(refItems);
    return [];
  };

  // Atomic INSTANCE-carrying items of any form — a ref (`Euro as active`) or a
  // string (`"eu" as active`, which parses but never means anything, §1.3).
  const instanceItems = (v: Value | undefined): { onString: boolean; instance: string; span?: SourceSpan }[] => {
    if (!v) return [];
    if (v.kind === "list") return v.items.flatMap(instanceItems);
    if (!("instance" in v) || !v.instance) return [];
    return [{ onString: v.kind === "string", instance: v.instance, span: v.instanceSource ?? v.source }];
  };

  /**
   * §1.3 instance-value rules, over one body's OWN aspect declarations:
   *   - `as` on a quoted place is inert (an implicit place has no scope) → error;
   *   - `as` on a non-module carrier is meaningless (membership is a module
   *     concept — a process/interface/surface is classified, never placed) → error.
   */
  const checkInstanceForms = (
    aspects: readonly AspectEntry[] | undefined,
    ownerType: "module" | "interface" | "surface" | "process",
    ownerKey: string
  ): void => {
    for (const a of aspects ?? []) {
      for (const it of instanceItems(a.value)) {
        if (it.onString) {
          diagnostics.push(
            makeDiag("error", it.span, "ASPECT_INSTANCE_ON_STRING", "validation.aspectInstanceOnString", {
              instance: it.instance,
              key: a.path.join("."),
            })
          );
        } else if (ownerType !== "module") {
          diagnostics.push(
            makeDiag("error", it.span, "ASPECT_INSTANCE_NOT_MODULE", "validation.aspectInstanceNotModule", {
              instance: it.instance,
              ownerType,
              ownerKey,
            })
          );
        }
      }
    }
  };

  // Walks the RESOLVED surface, not the decl: a surface's type name is a
  // registered type, so the parser can't tell `api Public { … }` from a module
  // and files it under `body.modules` — the resolver reclassifies it later.
  // Reading `decl.body.surfaces` therefore sees nothing at all, and the
  // instance check silently never ran for a surface carrier.
  const visitCarrierSurface = (f: ResolvedSurface, parent: readonly string[]): void => {
    const path = [...parent, f.name];
    checkInstanceForms(f.body.aspects, "surface", path.join("."));
    for (const i of f.interfaces) checkInstanceForms(i.body.aspects, "interface", [...path, i.name].join("."));
    for (const sub of f.surfaces) visitCarrierSurface(sub, path);
  };

  // §1.3: a membership whose member is ALREADY on the key's plane by a declared
  // source doesn't cross anything — the aspect is degenerate. Asked of the
  // declared-only sets, since the membership itself always pulls the member on.
  const declaredPlanes = model.presence?.declaredPlanesOf;
  const samePlaneReported = new Set<string>();
  const lintSamePlane = (memberKey: string, plane: string, span?: SourceSpan): void => {
    if (!declaredPlanes?.get(memberKey)?.has(plane)) return;
    const sig = `${memberKey}|${plane}`;
    if (samePlaneReported.has(sig)) return;
    samePlaneReported.add(sig);
    diagnostics.push(
      makeDiag("warning", span, "ASPECT_SAME_PLANE", "validation.aspectSamePlane", { member: memberKey, plane })
    );
  };

  // (place|plane) → instance name → member key (for the cross-member collision);
  // (member|place|plane) → instance (member-side, for the roster conflict).
  const instanceOwnerAt = new Map<string, Map<string, string>>();
  const memberInstance = new Map<string, string>();
  // §1.5: an instance name resolves BEFORE a same-named module, silently
  // shadowing it — nudge the author to rename. Emit once per shadowed name.
  const shadowLinted = new Set<string>();
  const lintShadow = (instance: string, span?: SourceSpan): void => {
    if (shadowLinted.has(instance) || !byLast.has(instance)) return;
    shadowLinted.add(instance);
    diagnostics.push(
      makeDiag(
        "warning",
        span,
        "INSTANCE_SHADOWS_MODULE",
        "validation.instanceShadowsModule",
        { instance }
      )
    );
  };
  const claim = (placeKey: string, plane: string, instance: string, memberKey: string, span?: SourceSpan): void => {
    const at = instanceOwnerAt.get(`${placeKey}|${plane}`) ?? new Map<string, string>();
    const prior = at.get(instance);
    if (prior !== undefined && prior !== memberKey) {
      diagnostics.push(
        makeDiag(
          "warning",
          span,
          "INSTANCE_NAME_COLLISION",
          "validation.instanceNameCollision",
          { instance, placeKey, prior }
        )
      );
    }
    at.set(instance, memberKey);
    instanceOwnerAt.set(`${placeKey}|${plane}`, at);
  };

  const visit = (m: ResolvedModule, parent: readonly string[]): void => {
    const path = [...parent, m.name];
    const memberKey = path.join(".");

    checkInstanceForms(m.decl.body.aspects, "module", memberKey);
    for (const i of m.decl.body.interfaces) {
      checkInstanceForms(i.body.aspects, "interface", [...path, i.name].join("."));
    }
    for (const f of m.surfaces) visitCarrierSurface(f, path);

    // Member-side aspects (`aspect deployment: Euro as active, …`).
    for (const l of m.decl.body.aspects) {
      const plane = l.path.join(".");
      const seen = new Set<string>();
      const items = refItems(l.value);
      if (items.length) lintSamePlane(memberKey, plane, l.source);
      for (const it of items) {
        const sig = `${it.path.join(".")}#${it.instance ?? ""}`;
        if (seen.has(sig)) {
          diagnostics.push(
            makeDiag(
              "error",
              it.span,
              "DUPLICATE_INSTANCE",
              "validation.duplicateAspectInstance",
              { path: it.path.join("."), hasInstance: it.instance ? "yes" : "no", instance: it.instance ?? "" }
            )
          );
          continue;
        }
        seen.add(sig);
        if (it.instance) lintShadow(it.instance, it.span);
        const place = resolveRef(it.path);
        if (place && it.instance) {
          claim(place, plane, it.instance, memberKey, it.span);
          memberInstance.set(`${memberKey}|${place}|${plane}`, it.instance);
        }
      }
    }

    // Place-side rosters (`aspect deployment for: A as active, …`).
    for (const r of m.decl.body.rosters ?? []) {
      const plane = r.key.join(".");
      const seen = new Set<string>();
      for (const e of r.entries) {
        const sig = `${e.path.join(".")}#${e.instance ?? ""}`;
        if (seen.has(sig)) {
          diagnostics.push(
            makeDiag(
              "error",
              e.instanceSource ?? e.source,
              "DUPLICATE_INSTANCE",
              "validation.duplicateRosterEntry",
              { path: e.path.join("."), hasInstance: e.instance ? "yes" : "no", instance: e.instance ?? "" }
            )
          );
          continue;
        }
        seen.add(sig);
        if (e.instance) lintShadow(e.instance, e.instanceSource ?? e.source);
        const member = resolveRef(e.path);
        if (member) lintSamePlane(member, plane, e.source);
        if (member && e.instance) {
          claim(memberKey, plane, e.instance, member, e.instanceSource ?? e.source);
          // Same membership named differently member-side vs here → conflict.
          const priorInstance = memberInstance.get(`${member}|${memberKey}|${plane}`);
          if (priorInstance !== undefined && priorInstance !== e.instance) {
            diagnostics.push(
              makeDiag(
                "warning",
                e.instanceSource ?? e.source,
                "ROSTER_MEMBER_CONFLICT",
                "validation.rosterMemberConflict",
                { member, instance: e.instance, priorInstance, plane }
              )
            );
          }
        }
      }
    }

    // `on` presence in a surface body is ignored (§1.2 — presence is a module
    // fact; a surface is an interaction grouping, not a placed element).
    for (const f of m.decl.body.surfaces) {
      const b = f.body;
      if (b.alsoOn?.length || b.partialOn?.length || b.rosters?.length) {
        diagnostics.push(
          makeDiag(
            "warning",
            f.typeSource ?? f.source,
            "SURFACE_ON_IGNORED",
            "validation.surfaceOnIgnored"
          )
        );
      }
    }

    for (const c of m.modules) visit(c, path);
  };
  for (const m of model.modules) visit(m, []);

  // Processes are aspect carriers too (v0.10 §5.1) — classification-only, like
  // every non-module carrier. They are hoisted flat onto the model, so this one
  // pass covers module-scoped and top-level alike.
  for (const p of model.processes) {
    checkInstanceForms(p.aspects, "process", [...(p.ownerPath ?? []), p.name].join("."));
  }
}

/** v0.9.0 §1.5: a process step referencing a module with no presence on the
 *  process's plane still renders but is FLAGGED — the cross-space soft-coupling
 *  treatment applied to planes. Unknown names (stubs, instance actors) are
 *  skipped; their own checks own them. */
export function validatePlanePresence(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  const presence = model.presence;
  if (!presence || presence.planes.length === 0) return;
  const check = (
    decl: { onPlanes?: readonly QualifiedName[]; ownerPath?: QualifiedName; source?: SourceSpan },
    body: Flow | undefined
  ): void => {
    const planes: readonly string[] = decl.onPlanes?.length
      ? decl.onPlanes.map((p) => p.join("."))
      : decl.ownerPath?.length
        ? [...(presence.planesOf.get(decl.ownerPath.join(".")) ?? ["primary"])]
        : ["primary"];
    forEachCallHop(body, (hop, node) => {
      for (const side of [hop.caller, hop.callee.modulePath]) {
        const key = side.join(".");
        const set = presence.planesOf.get(key);
        if (!set) continue;
        if (!planes.some((p) => set.has(p))) {
          diagnostics.push(
            makeDiag(
              "warning",
              node.source,
              "PLANE_MISMATCH",
              "validation.planeMismatch",
              { key, planes: planes.join(", "), firstPlane: planes[0] }
            )
          );
        }
      }
    });
  };
  for (const proc of model.processes) {
    check(proc, bodyForDecl(model.processModels, proc.source, proc.ownerPath));
  }
  for (const sub of model.subprocesses) {
    check(sub, bodyForDecl(model.subprocessModels, sub.source, sub.ownerPath));
  }
}

// =============================================================================
// Scopeable primitives — exported for the incremental compiler so it can run
// each validation pass over a single declaration's subtree (the dirty set) +
// prebuilt global indexes, instead of re-walking the whole model. `validate()`
// above keeps using the whole-model wrappers, so its output is byte-identical.
// =============================================================================

export type { SymbolIndex };
export { buildSymbolIndex, validateProcess, validateSubprocess };
// Re-export so the public API (index.ts `export *`) keeps `buildModuleCandidates`
// even though its implementation moved to hoist-index.
export { buildModuleCandidates } from "./hoist-index.js";

/** Local (per-decl, index-free) checks for a set of module subtrees: reserved
 *  names, required blanks, widget tags. Pure function of the resolved values —
 *  the incremental compiler caches this by module identity. */
export function localModuleDiagnostics(modules: readonly ResolvedModule[]): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  walkModules(modules, (m) => {
    if (RESERVED_NAMES.has(m.name)) {
      diagnostics.push(
        makeDiag("error", m.decl.nameSource ?? m.decl.source, "RESERVED_NAME", "validation.reservedModuleName", { name: m.name })
      );
    }
  });
  walkSurfaces(modules, (f) => {
    if (RESERVED_NAMES.has(f.name)) {
      diagnostics.push(
        makeDiag("error", f.decl.nameSource ?? f.decl.source, "RESERVED_NAME", "validation.reservedSurfaceName", { name: f.name })
      );
    }
  });
  walkInterfaces(modules, (i) => {
    if (RESERVED_NAMES.has(i.name)) {
      diagnostics.push(
        makeDiag("error", i.decl.nameSource ?? i.decl.source, "RESERVED_NAME", "validation.reservedInterfaceName", { name: i.name })
      );
    }
  });
  walkModules(modules, (m) => checkBodyForBlanks(m.body, m.name, m.decl.source, diagnostics));
  walkModules(modules, (m) => checkWidgetField(m.body.fields, diagnostics));
  walkSurfaces(modules, (f) => checkWidgetField(f.body.fields, diagnostics));
  diagnostics.push(...unnamedModuleDiagnostics(modules));
  return diagnostics;
}

/**
 * Unnamed-module rules (§4.14). An unnamed declaration took its name from its
 * type (snake_case → UpperCamelCase). Enforced here so both the cold path
 * (`validate`) and the incremental path (`localModuleDiagnostics`) share one
 * implementation — diverging would break the cold == delta invariant.
 *
 *   - a base `module` may not be unnamed                  → UNNAMED_BASE_MODULE
 *   - two unnamed siblings of the same type collide       → UNNAMED_DUPLICATE_TYPE
 *   - only modules may be unnamed (a surface/interface)   → UNNAMED_NON_MODULE
 */
export function unnamedModuleDiagnostics(modules: readonly ResolvedModule[]): Diagnostic[] {
  const out: Diagnostic[] = [];
  const visit = (m: ResolvedModule): void => {
    // Module checks count the RAW (pre-merge) body children. §4.14 (LOCKED):
    // an applied type may be left unnamed only when it is used EXACTLY ONCE in
    // the parent — "if a type appears more than once, every one must be named,
    // no 'one unnamed + the rest named'." So count ALL siblings by applied type
    // (named and unnamed alike); any unnamed one whose type is used >1 collides.
    const typeCount = new Map<string, number>();
    for (const md of m.decl.body.modules) {
      typeCount.set(md.typeName, (typeCount.get(md.typeName) ?? 0) + 1);
    }
    for (const md of m.decl.body.modules) {
      if (!md.unnamed) continue;
      if (md.name.length === 0 || !/^[A-Za-z_]/.test(md.name)) {
        // A type with no usable letters (e.g. all underscores) derives an empty
        // or non-identifier name — there is nothing to stand in for the name.
        out.push(
          makeDiag(
            "error",
            md.typeSource ?? md.source,
            "UNNAMED_BAD_DERIVED_NAME",
            "validation.unnamedBadDerivedName",
            { typeName: md.typeName }
          )
        );
      } else if (md.typeName === "module") {
        out.push(
          makeDiag(
            "error",
            md.typeSource ?? md.source,
            "UNNAMED_BASE_MODULE",
            "validation.unnamedBaseModule"
          )
        );
      } else if ((typeCount.get(md.typeName) ?? 0) > 1) {
        out.push(
          makeDiag(
            "error",
            md.typeSource ?? md.source,
            "UNNAMED_DUPLICATE_TYPE",
            "validation.unnamedDuplicateType",
            { typeName: md.typeName }
          )
        );
      }
    }
    // Non-modules are classified by the registry, so read them off the resolved
    // surfaces/interfaces — only modules may be unnamed.
    for (const f of m.surfaces) {
      if (f.decl.unnamed) {
        out.push(
          makeDiag(
            "error",
            f.decl.typeSource ?? f.decl.source,
            "UNNAMED_NON_MODULE",
            "validation.unnamedSurface",
            { typeName: f.typeName }
          )
        );
      }
    }
    // v0.9.0 §1.6: an unnamed interface is legal ONLY when braced — it is then
    // the module's DEFAULT interface. A bare unnamed `interface` still needs a
    // name; a second default in one module collides.
    for (const i of m.interfaces) {
      if (i.decl.unnamed && !i.decl.hasBody) {
        out.push(
          makeDiag(
            "error",
            i.decl.typeSource ?? i.decl.source,
            "UNNAMED_NON_MODULE",
            "validation.unnamedInterfaceDefault",
            { decl: `${i.typeName} {}` }
          )
        );
      }
    }
    // The single-default check counts the RAW (pre-merge) decls: two
    // `interface {}` derive the same name ("Interface") and the resolver merges
    // them into one, so `m.interfaces` would show only one. Interface-routed
    // decls live in `body.interfaces`; a same-type user-interface-kind duplicate
    // (`myproto {}` twice) is caught earlier by UNNAMED_DUPLICATE_TYPE.
    const rawDefaults = m.decl.body.interfaces.filter((i) => i.unnamed && i.hasBody);
    const resolvedDefaults = m.interfaces.filter((i) => i.decl.unnamed && i.decl.hasBody);
    if (Math.max(rawDefaults.length, resolvedDefaults.length) > 1) {
      const second = rawDefaults[1] ?? resolvedDefaults[1]?.decl;
      out.push(
        makeDiag(
          "error",
          second?.typeSource ?? second?.source,
          "DEFAULT_INTERFACE_DUPLICATE",
          "validation.defaultInterfaceDuplicate",
          { typeName: second?.typeName ?? "interface" }
        )
      );
    }
    // Two interfaces resolving to the same NAME in one raw body merge silently
    // (a default `interface {}` derives "Interface", colliding with a named
    // `Interface`; `rest_read Same` + `grpc Same` collide too). The author's two
    // declared interaction points become one — warn so it isn't lost.
    const ifaceByName = new Map<string, InterfaceDecl>();
    for (const i of m.decl.body.interfaces) {
      const prior = ifaceByName.get(i.name);
      if (prior) {
        out.push(
          makeDiag(
            "warning",
            i.nameSource ?? i.typeSource ?? i.source,
            "DUPLICATE_INTERFACE",
            "validation.duplicateInterface",
            { name: i.name, module: m.name }
          )
        );
      } else {
        ifaceByName.set(i.name, i);
      }
    }
    for (const child of m.modules) visit(child);
  };
  for (const m of modules) visit(m);
  return out;
}

/** Reserved-keyword check for type declarations. */
export function typeNameDiagnostics(types: readonly { name: string; nameSource?: SourceSpan; source?: SourceSpan }[]): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  for (const t of types) {
    if (RESERVED_NAMES.has(t.name)) {
      diagnostics.push(
        makeDiag("error", t.nameSource ?? t.source, "RESERVED_NAME", "validation.reservedTypeName", { name: t.name })
      );
    }
  }
  return diagnostics;
}

/** Aspect-reference checks for a set of module subtrees against a prebuilt
 *  symbol index. */
export function aspectRefDiagnostics(scope: ResolvedModel, index: SymbolIndex): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  forEachAspectValue(scope, (occ) => {
    if (occ.cls !== "ref") return;
    const name =
      occ.value.kind === "identifier"
        ? occ.value.text
        : occ.value.kind === "ref"
          ? occ.value.path.join(".")
          : "";
    if (index.modules.has(name)) return;
    // Dotted refs may be written as a path-suffix (hoisting-lite): accept a
    // unique last-segment candidate whose qualified path ends with the ref.
    const last = name.split(".").pop() ?? "";
    const cands = index.moduleCandidates.get(last) ?? [];
    if (cands.some((c) => c === name || c.endsWith("." + name))) return;
    const key = occ.keyPath.join(".");
    diagnostics.push(
      makeDiag("error", occ.source, "ASPECT_BAD_REF", "validation.aspectBadRef", { key, name })
    );
  });
  return diagnostics;
}

/** Description-content checks for a set of module subtrees + types against a
 *  prebuilt description index. */
/** A node bearing a description: everything the `@`/`@@` axes need, plus where
 *  it was written. Modules/surfaces/interfaces carry decorations on `.decl`,
 *  a type carries them directly — so callers pass the carrier, not the parts. */
interface DescribedDecl {
  readonly source?: SourceSpan;
  readonly decorations?: readonly DecorationInstance[];
}

/** The part of a body the two getter axes read. Structural rather than `Body`,
 *  because an interface's is an `InterfaceBody` and both are walked here. */
type DescribedBody = Pick<Body, "descriptions" | "aspects" | "fields">;

export function descriptionDiagnosticsScoped(
  modules: readonly ResolvedModule[],
  types: readonly ({ body: DescribedBody } & DescribedDecl)[],
  index: ReturnType<typeof buildDescriptionIndex>,
  spaces: ResolvedModel["spaces"]
): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const spaceOf = (file?: string) => (file ? spaces?.get(file) : undefined);
  // The `@` axis is decorations then fields (§4.3.1), and the vocabulary rides
  // on the index so there is exactly ONE way to obtain it. It used to be a
  // separate optional parameter, which the incremental caller silently did not
  // pass — leaving the primary-field sugar unresolvable and reporting the
  // spec's own `@planned.from` example as a missing field while a cold compile
  // of the same text was clean.
  const run = (body: DescribedBody, decl: DescribedDecl): void => {
    if (body.descriptions.length === 0) return;
    const ctx = nodeDescriptionContext(
      index,
      { ...body, decorations: decl.decorations },
      spaceOf(decl.source?.file)
    );
    for (const entry of body.descriptions) {
      const { issues } = renderDescription(entry.text, ctx);
      for (const issue of issues) diagnostics.push(diagFromIssue(issue, entry.source));
    }
  };
  walkModules(modules, (m) => run(m.body, m.decl));
  walkSurfaces(modules, (f) => run(f.body, f.decl));
  walkInterfaces(modules, (i) => run(i.body, i.decl));
  for (const t of types) run(t.body, t);
  return diagnostics;
}

/**
 * An aspect value's lexical form decides its cross-cutting meaning:
 *   - string  `aspects.k: "x"`  → an aspect (group identity) — always valid.
 *   - bare    `aspects.k: x`    → a reference to a module named `x`.
 *
 * References are strict: a bare value must resolve to a real module
 * (matching the existing reference semantics for process steps
 * and `in <Parent>`). Quoting the value turns it into an
 * aspect, which is the escape hatch surfaced in the message + LSP
 * quick-fix.
 */
function validateAspectRefs(
  model: ResolvedModel,
  index: SymbolIndex,
  diagnostics: Diagnostic[]
): void {
  forEachAspectValue(model, (occ) => {
    if (occ.cls !== "ref") return;
    const name =
      occ.value.kind === "identifier"
        ? occ.value.text
        : occ.value.kind === "ref"
          ? occ.value.path.join(".")
          : "";
    if (index.modules.has(name)) return;
    // Dotted refs may be written as a path-suffix (hoisting-lite): accept a
    // unique last-segment candidate whose qualified path ends with the ref.
    const last = name.split(".").pop() ?? "";
    const cands = index.moduleCandidates.get(last) ?? [];
    if (cands.some((c) => c === name || c.endsWith("." + name))) return;
    const key = occ.keyPath.join(".");
    diagnostics.push(
      makeDiag(
        "error",
        occ.source,
        "ASPECT_BAD_REF",
        "validation.aspectBadRef",
        { key, name }
      )
    );
  });
}

/**
 * Surface obvious-bad `widget:` values. We can't validate against a
 * live custom-elements registry from the LSP, but we can at least
 * catch HTML-invalid identifiers — a custom-element name MUST contain
 * a hyphen and MUST start with a lowercase letter. `widget: archmodule`
 * (missing dash) and `widget: ArchModule` (PascalCase) both fail the
 * spec and won't register at runtime. Identifiers that pass the
 * structural check are accepted — the assumption is that legal
 * custom-element names exist in some widget JS file we don't have to
 * cross-reference.
 *
 * String-form values (inline templates) and aspect-prefixed values are
 * ignored — those have their own semantics.
 */
function validateWidgetTags(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  walkModules(model.modules, (m) => checkWidgetField(m.body.fields, diagnostics));
  walkSurfaces(model.modules, (f) => checkWidgetField(f.body.fields, diagnostics));
}

function checkWidgetField(fields: readonly FieldDecl[], diagnostics: Diagnostic[]): void {
  for (const f of fields) {
    if (f.path.length !== 1 || f.path[0] !== "widget") continue;
    if (!f.value || f.value.kind !== "identifier") continue;
    const tag = f.value.text;
    // Custom-element name validation per the HTML spec (PotentialCustomElementName):
    // must start with [a-z], must contain at least one hyphen, and only contain
    // the (very permissive) PCEN char set. We use a narrower ASCII set since
    // archlang identifiers are ASCII anyway.
    const valid = /^[a-z][a-z0-9._-]*-[a-z0-9._-]*$/.test(tag);
    if (!valid) {
      diagnostics.push(
        makeDiag("warning", f.source, "WIDGET_TAG_INVALID", "validation.widgetTagInvalid", { tag })
      );
    }
  }
}

// =============================================================================
// Description content validation (Spec §4.11)
// =============================================================================

/**
 * Walk every description string in the model, resolve `[[…]]` refs and
 * `@aspect` interpolations, and emit a diagnostic for each unresolved /
 * ambiguous / missing case.
 *
 * Source spans point at the owning description entry (the whole string
 * literal). Sub-string offsets within a description are reported in the
 * `DescriptionIssue` but not translated to file positions — that
 * mapping requires accounting for source-string escapes and is left to
 * downstream tooling.
 */
/**
 * Validate markdown documents (§4.15): a `.md` body is rendered with the same
 * flavored-markdown grammar, surfacing bare `@` interpolations (forbidden — no
 * owning node) and unresolved element / aspect / document-section refs. Issues
 * point at the document path.
 */
export function documentDiagnostics(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  const docs = model.documents;
  if (!docs || docs.length === 0) return;
  const index = buildDescriptionIndex(model, docs);
  for (const doc of docs) {
    const ctx = makeDescriptionContext(index, () => undefined, doc.space);
    const { issues } = renderMarkdownDocument(doc.body, ctx);
    for (const issue of issues) {
      const span: SourceSpan = {
        start: { line: 0, column: 0, offset: issue.offset },
        end: { line: 0, column: 0, offset: issue.offset + issue.length },
        file: doc.path,
      };
      diagnostics.push(diagFromIssue(issue, span));
    }
  }
}

function diagFromIssue(
  issue: DescriptionIssue,
  source: SourceSpan | undefined
): Diagnostic {
  switch (issue.kind) {
    case "unresolved-ref":
      return makeDiag(
        "warning",
        source,
        "DESC_UNRESOLVED_REF",
        "validation.descUnresolvedRef",
        { text: issue.text }
      );
    case "ambiguous-ref": {
      // Sort candidates so the message is deterministic regardless of the
      // index's insertion order (the incremental compiler builds it per-root,
      // not in whole-model walk order).
      const names = (issue.candidates ?? [])
        .map((c) => c.qualifiedName)
        .sort()
        .join(", ");
      return makeDiag(
        "warning",
        source,
        "DESC_AMBIGUOUS_REF",
        "validation.descAmbiguousRef",
        { text: issue.text, names }
      );
    }
    case "missing-aspect":
      return makeDiag(
        "warning",
        source,
        "DESC_MISSING_ASPECT",
        "validation.descMissingAspect",
        { text: issue.text }
      );
    case "missing-field":
      return makeDiag(
        "warning",
        source,
        "DESC_MISSING_FIELD",
        "validation.descMissingField",
        { text: issue.text }
      );
    case "bare-interpolation":
      return makeDiag(
        "error",
        source,
        "DOC_BARE_INTERPOLATION",
        "validation.docBareInterpolation",
        { text: issue.text }
      );
    case "calc-error":
      return makeDiag(
        "warning",
        source,
        "DESC_CALC_ERROR",
        "validation.descCalcError",
        { message: issue.message ?? "invalid expression", hasText: issue.text ? "yes" : "no", text: issue.text ?? "" }
      );
  }
}

/**
 * Backwards-compat overload that accepts a `ParsedFile`. Validation
 * meaningfully requires resolution; if a `ParsedFile` is passed, return
 * an empty result and recommend the resolved-model path.
 */
export function validateParsedFile(_file: ParsedFile): ValidationResult {
  return { diagnostics: [] };
}

// =============================================================================
// Reserved-keyword name validation
// =============================================================================

function validateNames(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  for (const t of model.types) {
    if (RESERVED_NAMES.has(t.name)) {
      diagnostics.push(
        makeDiag(
          "error",
          t.nameSource ?? t.source,
          "RESERVED_NAME",
          "validation.reservedTypeName",
          { name: t.name }
        )
      );
    }
  }
  walkModules(model.modules, (m) => {
    if (RESERVED_NAMES.has(m.name)) {
      diagnostics.push(
        makeDiag(
          "error",
          m.decl.nameSource ?? m.decl.source,
          "RESERVED_NAME",
          "validation.reservedModuleName",
          { name: m.name }
        )
      );
    }
  });
  walkSurfaces(model.modules, (f) => {
    if (RESERVED_NAMES.has(f.name)) {
      diagnostics.push(
        makeDiag(
          "error",
          f.decl.nameSource ?? f.decl.source,
          "RESERVED_NAME",
          "validation.reservedSurfaceName",
          { name: f.name }
        )
      );
    }
  });
  walkInterfaces(model.modules, (i) => {
    if (RESERVED_NAMES.has(i.name)) {
      diagnostics.push(
        makeDiag(
          "error",
          i.decl.nameSource ?? i.decl.source,
          "RESERVED_NAME",
          "validation.reservedInterfaceName",
          { name: i.name }
        )
      );
    }
  });
}

// =============================================================================
// Required-blank validation
// =============================================================================

function validateRequiredBlanks(model: ResolvedModel, diagnostics: Diagnostic[]): void {
  walkModules(model.modules, (m) => {
    checkBodyForBlanks(m.body, m.name, m.decl.source, diagnostics);
  });
}

function checkBodyForBlanks(
  body: Body,
  ownerName: string,
  ownerSource: SourceSpan | undefined,
  diagnostics: Diagnostic[]
): void {
  for (const f of body.fields) {
    if (f.required && f.value === undefined) {
      diagnostics.push(
        makeDiag(
          "error",
          f.source ?? ownerSource,
          "REQUIRED_NOT_FILLED",
          "validation.requiredNotFilled",
          { kind: "field", name: f.path.join("."), owner: ownerName }
        )
      );
    }
  }
  for (const l of body.aspects) {
    if (l.required && l.value === undefined && !l.drop) {
      diagnostics.push(
        makeDiag(
          "error",
          l.source ?? ownerSource,
          "REQUIRED_NOT_FILLED",
          "validation.requiredNotFilled",
          { kind: "aspect", name: l.path.join("."), owner: ownerName }
        )
      );
    }
  }
  // Required sub-declarations: check if their bodies are still empty
  // (no content fulfilling the blank).
  for (const f of body.surfaces) {
    if (f.required && isSurfaceBlank(f.body)) {
      diagnostics.push(
        makeDiag(
          "error",
          f.source ?? ownerSource,
          "REQUIRED_NOT_FILLED",
          "validation.requiredNotFulfilled",
          { kind: "surface", name: f.name, owner: ownerName }
        )
      );
    } else if (f.required) {
      diagnostics.push(requiredWithContent("surface", f.name, ownerName, f.source ?? ownerSource));
    }
  }
  for (const m of body.modules) {
    if (m.required && isModuleBlank(m.body)) {
      diagnostics.push(
        makeDiag(
          "error",
          m.source ?? ownerSource,
          "REQUIRED_NOT_FILLED",
          "validation.requiredNotFulfilled",
          { kind: "module", name: m.name, owner: ownerName }
        )
      );
    } else if (m.required) {
      diagnostics.push(requiredWithContent("module", m.name, ownerName, m.source ?? ownerSource));
    }
  }
  for (const i of body.interfaces) {
    if (i.required && isInterfaceBodyBlank(i.body)) {
      diagnostics.push(
        makeDiag(
          "error",
          i.source ?? ownerSource,
          "REQUIRED_NOT_FILLED",
          "validation.requiredNotFulfilled",
          { kind: "interface", name: i.name, owner: ownerName }
        )
      );
    } else if (i.required) {
      diagnostics.push(requiredWithContent("interface", i.name, ownerName, i.source ?? ownerSource));
    }
  }
}

// §4.8 invariant: `required` (a mandatory blank) and providing content are
// mutually exclusive. `required component logs { rest_create Send }` is a
// contradiction — a blank cannot carry content — and is rejected here.
function requiredWithContent(
  kind: string,
  name: string,
  ownerName: string,
  source: SourceSpan | undefined
): Diagnostic {
  return makeDiag(
    "error",
    source,
    "REQUIRED_HAS_CONTENT",
    "validation.requiredHasContent",
    { kind, name, owner: ownerName }
  );
}

function isSurfaceBlank(body: Body): boolean {
  return (
    body.fields.length === 0 &&
    body.aspects.length === 0 &&
    body.surfaces.length === 0 &&
    body.interfaces.length === 0
  );
}

function isModuleBlank(body: Body): boolean {
  return (
    body.fields.length === 0 &&
    body.aspects.length === 0 &&
    body.surfaces.length === 0 &&
    body.interfaces.length === 0 &&
    body.modules.length === 0
  );
}

function isInterfaceBodyBlank(body: { fields: readonly unknown[]; aspects: readonly unknown[] }): boolean {
  return body.fields.length === 0 && body.aspects.length === 0;
}

// =============================================================================
// Process step validation
// =============================================================================

interface SymbolIndex {
  /** Set of all qualified module/actor names (as dot-strings). */
  modules: Set<string>;
  /** Set of all qualified interface names (leaves, as dot-strings). */
  interfaces: Set<string>;
  /** Leaf short-name → every qualified path bearing it (v0.8.2 hoisting). */
  moduleCandidates: Map<string, string[]>;
  /** §4.11 interface spellings (owner suffixes + surface-ELIDED forms) → the
   *  space-tagged FULL structural paths behind each — the ambiguity lists +
   *  did-you-mean full-path source for interface callees. */
  ifaceCandidates: Map<string, ScopedCand<string>[]>;
  /** v0.8.2 §4.11 space-scoping (MULTI-space models only). Present ⇒ ambiguity is
   *  computed within the referrer's space chain, not globally. */
  spaces?: ReadonlyMap<string, QualifiedName>;
  scopedModules?: Map<string, ScopedCand<ResolvedModule>[]>;
}

/**
 * v0.8.2 hoisting — the candidates a module reference `name` hoists to, when it is
 * AMBIGUOUS: 2+ modules carry the suffix and none is a top-level exact match (a
 * top-level module of that name wins outright). In a MULTI-space model ambiguity
 * is scoped to the referrer's space chain (§4.11) — a suffix ambiguous globally
 * but unique in the nearest visible space is NOT ambiguous. Returns the candidate
 * paths when ambiguous, else undefined.
 */
function ambiguousModuleCandidates(
  index: SymbolIndex,
  name: string,
  referrerSpace?: QualifiedName
): readonly string[] | undefined {
  if (!index.scopedModules) {
    // Single-space: global ambiguity, unchanged.
    const cands = index.moduleCandidates.get(name);
    if (!cands || cands.length < 2) return undefined;
    if (cands.includes(name)) return undefined; // top-level exact wins
    return cands;
  }
  // Multi-space: nearest-first over the referrer's space chain. The closest space
  // that has any match decides — an exact full-path or a unique suffix there
  // resolves; 2+ non-exact there are ambiguous.
  const cands = index.scopedModules.get(name);
  if (!cands || cands.length < 2) return undefined;
  for (const s of spaceChain(referrerSpace)) {
    const here = cands.filter((c) => sameSpace(c.space, s));
    if (here.length === 0) continue;
    if (here.some((c) => c.path.join(".") === name)) return undefined; // exact wins here
    if (here.length === 1) return undefined; // unique suffix in this space
    return here.map((c) => c.path.join(".")); // ambiguous in the nearest space
  }
  // Nothing on the chain → distinct globals stay ambiguous, a sole target resolves.
  const distinct = [...new Set(cands.map((c) => c.path.join(".")))];
  return distinct.length > 1 ? distinct : undefined;
}

function buildSymbolIndex(model: ResolvedModel): SymbolIndex {
  // Membership sets are shared with the stub classifier (`buildSymbolSets`) so
  // validation and the callee classifier agree on what a hoisted reference names;
  // `moduleCandidates`/`ifaceCandidates` add the ambiguity lists on top.
  const { modules, interfaces } = buildSymbolSets(model);
  const multiSpace = !!(model.spaces && model.spaces.size > 0);
  return {
    modules: new Set(modules),
    interfaces: new Set(interfaces),
    moduleCandidates: buildModuleCandidates(model.modules),
    ifaceCandidates: buildIfaceCandidates(model),
    ...(multiSpace
      ? { spaces: model.spaces, scopedModules: buildScopedModuleIndex(model) }
      : {}),
  };
}

/**
 * §4.11 surface elision — the candidates an interface reference hoists/elides
 * to, when it is AMBIGUOUS: 2+ declared interfaces answer to the written
 * spelling and none is exact. Exact structural paths win outright (ladder step
 * 1): a declared interface at the written path, or a MODULE there (it resolves
 * via the module ladder — default interface / anonymous handler). In a
 * MULTI-space model, ambiguity is scoped to the referrer's space chain like
 * module hoisting — a spelling ambiguous globally but unique in the nearest
 * visible space is NOT ambiguous. Returns the candidate full paths when
 * ambiguous, else undefined.
 */
function ambiguousIfaceCandidates(
  index: SymbolIndex,
  written: string,
  referrerSpace?: QualifiedName
): readonly string[] | undefined {
  const cands = index.ifaceCandidates.get(written);
  if (!cands) return undefined;
  const distinct = [...new Set(cands.map((c) => c.value))];
  if (distinct.length < 2) return undefined;
  if (distinct.includes(written)) return undefined; // exact interface path wins
  if (index.moduleCandidates.get(written)?.includes(written)) return undefined; // exact module path wins
  if (index.spaces) {
    // Multi-space: nearest-first over the referrer's chain, mirroring the
    // scoped edge resolution (`resolveScoped`) so the diagnostic and the graph
    // agree on what resolves.
    for (const s of spaceChain(referrerSpace)) {
      const here = [...new Set(cands.filter((c) => sameSpace(c.space, s)).map((c) => c.value))];
      if (here.length === 0) continue;
      if (here.length === 1) return undefined; // unique in the nearest space
      return here;
    }
  }
  return distinct;
}

const NO_CONFLICTS: ReadonlySet<string> = new Set();


function validateProcess(
  proc: ProcessDecl,
  index: SymbolIndex,
  model: ResolvedModel,
  diagnostics: Diagnostic[],
  roleConflicts: ReadonlySet<string> = NO_CONFLICTS
): void {
  // v0.9.0 §2.8 (TODO split): an anonymous bare-step run is legitimate mature
  // content — "B calls A" as a one-liner needs no named `process` wrapper, so
  // it raises NO TODO. The draft-floor gate lives on the CALLEE side instead:
  // every callee must resolve to a declared interface
  // (PROCESS_CALLEE_UNSPECIFIED / PROCESS_CALLEE_TODO below), so a module with
  // ten `> X` lines still carries ten TODOs until each X earns a surface.
  validateHeaderFields(proc, diagnostics);
  const subLookup = buildSubprocessLookup(proc, model);
  const body = bodyForDecl(model.processModels, proc.source, proc.ownerPath);
  const binds = collectBindNames(body);
  // v0.9.0 §1.5: membership instance names at the process's owner place are
  // legal actors (`active > standby` inside `in Euro process Replication`).
  const instances = model.presence?.instancesAt.get((proc.ownerPath ?? []).join("."));
  if (instances) for (const n of instances.keys()) binds.add(n);
  validateFlow(body, index, subLookup, processNamesFor(model, proc.ownerPath ?? []), binds, roleConflicts, diagnostics, model, proc.ownerPath ?? []);
  // Process-NESTED subprocesses (`process P { subprocess X on C { … } }`) are not
  // in `model.subprocesses` (only top-level + module-scoped are), so the
  // whole-model subprocess loop never reaches them — validate them here, else a
  // callee referenced only inside one gets a silent stub but no PROCESS_CALLEE_TODO.
  for (const sub of proc.subprocesses ?? []) {
    validateSubprocess(sub, index, model, diagnostics, roleConflicts);
  }
}

/**
 * Validate a subprocess body's steps. Without this, callees reached only inside
 * a subprocess (invoked via `do`) were never checked — an undefined one got a
 * silent stub but no `PROCESS_CALLEE_TODO`, so it slipped past the zero-TODO
 * completeness gate. The subprocess's `params` are pre-bound names (legal
 * callers), seeded into the bind set exactly like a `name =` bind.
 */
function validateSubprocess(
  sub: SubprocessDecl,
  index: SymbolIndex,
  model: ResolvedModel,
  diagnostics: Diagnostic[],
  roleConflicts: ReadonlySet<string> = NO_CONFLICTS
): void {
  const subLookup = buildSubprocessLookup(sub, model);
  const binds = new Set<string>(sub.params);
  // The `on Caller` template default-owner is a bound name — a legal caller for
  // the body's owner-omitted heads (which the resolver fills with it).
  if (sub.onCaller) binds.add(sub.onCaller);
  validateHeaderFields(sub, diagnostics);
  const body = bodyForDecl(model.subprocessModels, sub.source, sub.ownerPath);
  for (const b of collectBindNames(body)) binds.add(b);
  validateFlow(body, index, subLookup, processNamesFor(model, sub.ownerPath ?? []), binds, roleConflicts, diagnostics, model, sub.ownerPath ?? []);
  // Recurse into subprocess-nested subprocesses for the same reason.
  for (const child of sub.subprocesses ?? []) {
    validateSubprocess(child, index, model, diagnostics, roleConflicts);
  }
}

/**
 * Owner-TODO nudge (PROCESS.md §2 "who decides this"): a CONTROL node — a
 * decision (`if`/`select`), fork (`parallel`), loop (`each`/retry), guard
 * (`try`), saga (`reversible`), or wait (`await`) — whose owner can't resolve
 * (no prefix, no enclosing owner, no module) renders with a blank performer.
 * Surface it as a TODO so it shows up in the `--complete` gate. Reads the
 * resolved ProcessModel IR (owners are resolved there). Calls/notes are skipped:
 * an unowned call head is already PROCESS_ANON_HEAD_AT_ROOT / a caller-TODO.
 * `dist` is a RESOLVED (emergent) owner, not a TODO.
 */
export function validateProcessOwners(models: readonly { readonly body?: Flow }[], diagnostics: Diagnostic[]): void {
  const aspects: Record<string, string> = {
    branch: "decision", select: "decision", loop: "loop", retry: "retry loop",
    guard: "try/catch", parallel: "parallel split", reversible: "reversible saga",
    await: "wait", owner_block: "owner block",
  };
  const flag = (
    kind: string,
    owner: { form: string; resolved?: readonly string[] } | undefined,
    source: SourceSpan | undefined
  ): void => {
    // Unowned: `none`, OR a `ditto`/`inherited` that resolved to nothing (a ditto
    // with no previous sibling and no enclosing owner). `dist`/`explicit` are
    // always resolved owners — never flagged.
    const unowned =
      owner != null &&
      (owner.form === "none" ||
        ((owner.form === "ditto" || owner.form === "inherited") &&
          (owner.resolved == null || owner.resolved.length === 0)));
    if (unowned) {
      diagnostics.push(
        makeDiag(
          "todo",
          source,
          "PROCESS_OWNER_TODO",
          "validation.processOwnerTodo",
          { label: aspects[kind] ?? kind }
        )
      );
    }
  };
  // Per-process re-entry aspects: every `as <name>` defined, and every `go <name>`
  // reference, so a `go` to a non-existent step surfaces as a TODO.
  let asNames = new Set<string>();
  let asDefs: { name: string; source: SourceSpan | undefined }[] = [];
  let gos: { name: string; source: SourceSpan | undefined }[] = [];
  const addAs = (ref: { name: string; source?: SourceSpan } | undefined): void => {
    if (!ref) return;
    asNames.add(ref.name);
    asDefs.push({ name: ref.name, source: ref.source });
  };
  // The container recursion is the shared `forEachNode` — this only decides, per
  // node, whether it's an owner-flag candidate (a control node) and collects
  // `as`/`go` for the re-entry checks. (note: an unowned call head is flagged by
  // the AST validator; terminator/splice/description carry no owner or re-entry.)
  const CONTROL: ReadonlySet<string> = new Set([
    "branch", "select", "loop", "retry", "guard", "parallel", "reversible", "owner_block", "await",
  ]);
  for (const m of models) {
    asNames = new Set();
    asDefs = [];
    gos = [];
    forEachNode(m.body, (n) => {
      if (n.kind === "call") addAs(n.asName);
      else if (n.kind === "reentry") gos.push({ name: n.target.name, source: n.source });
      else if (CONTROL.has(n.kind)) {
        flag(n.kind, (n as { owner?: { form: string; resolved?: readonly string[] } }).owner, n.source);
        if (n.kind === "reversible") for (const it of n.items) addAs(it.asName);
      }
    });
    // A re-entry label must be unique within a process — two `as <name>` steps
    // make a `go <name>` ambiguous (which step does it resume at?). Flag every
    // occurrence after the first.
    const asSeen = new Set<string>();
    for (const a of asDefs) {
      if (asSeen.has(a.name)) {
        diagnostics.push(
          makeDiag(
            "warning",
            a.source,
            "PROCESS_DUPLICATE_AS",
            "validation.processDuplicateAs",
            { name: a.name }
          )
        );
      }
      asSeen.add(a.name);
    }
    // v0.9.0 §2.4 terminate-all checks:
    //  - `finish all` inside a reversible with unwind-bearing steps is a
    //    warned no-compensation stop (BPMN terminate does not compensate);
    //  - an `as`-label that only occurs AFTER a terminate-all in its own
    //    linear flow is unreachable for `go` (same-flow approximation).
    const unreachableAs = new Set<string>();
    const scanTerminateAll = (flow: Flow | undefined, revHasUnwinds: boolean): void => {
      if (!flow) return;
      let terminated = false;
      const sub = (f: Flow | undefined, rev = revHasUnwinds): void => scanTerminateAll(f, rev);
      for (const n of flow as readonly ProcessNode[]) {
        if (terminated && n.kind === "call" && n.asName) unreachableAs.add(n.asName.name);
        switch (n.kind) {
          case "terminator": {
            const t = n as TerminatorNode;
            if (t.all) {
              terminated = true;
              if (t.outcome === "finish" && revHasUnwinds) {
                diagnostics.push(
                  makeDiag(
                    "warning",
                    t.source,
                    "PROCESS_FINISH_ALL_NO_COMPENSATION",
                    "validation.processFinishAllNoCompensation"
                  )
                );
              }
            }
            break;
          }
          case "branch":
            for (const a of n.arms) sub(a.body);
            sub(n.elseArm?.body);
            break;
          case "select":
            for (const c of n.cases) sub(c.body);
            break;
          case "loop":
            sub(n.body);
            break;
          case "retry":
            sub(n.attempt);
            for (const c of n.catches) sub(c.body);
            sub(n.elseBody);
            break;
          case "guard":
            sub(n.attempt);
            for (const c of n.catches) sub(c.body);
            break;
          case "parallel":
            if (n.body.form === "fanOut") sub(n.body.body);
            else for (const b of n.body.branches) sub(b.body);
            sub(n.awaitClause?.elseBody);
            break;
          case "reversible": {
            const hasUnwinds = n.items.some((it) => it.unwind);
            for (const it of n.items) {
              if (!it.forward) continue;
              if (it.forward.kind === "block") sub(it.forward.body, hasUnwinds);
              else sub([it.forward], hasUnwinds);
            }
            break;
          }
          case "owner_block":
            sub(n.body);
            break;
          default:
            break;
        }
      }
    };
    scanTerminateAll(m.body, false);

    // A `go` with no matching `as` in the same process is a dangling re-entry —
    // a draft-floor TODO (process-first), not an error.
    for (const g of gos) {
      if (asNames.has(g.name) && unreachableAs.has(g.name)) {
        diagnostics.push(
          makeDiag(
            "warning",
            g.source,
            "PROCESS_GO_UNREACHABLE",
            "validation.processGoUnreachable",
            { name: g.name }
          )
        );
      }
      if (!asNames.has(g.name)) {
        diagnostics.push(
          makeDiag(
            "todo",
            g.source,
            "PROCESS_GO_TODO",
            "validation.processGoTodo",
            { name: g.name }
          )
        );
      }
    }
  }
}

/** The resolved IR `Flow` for a process/subprocess declaration, paired by source
 *  position (`file:offset`) and owner. Steps on the decl are the written form;
 *  the IR is where callers are resolved, and the declaration's own span is
 *  identical in both, so the position is a stable key.
 *
 *  Stamped (type-template) subprocesses yield several IR bodies sharing one
 *  source, and they are NOT interchangeable: an omitted head resolves to the
 *  STAMPING module, so each instance's resolved body calls out from a different
 *  place. Matching on source alone hands the declaration whichever instance was
 *  built first, which fabricates diagnostics naming a module that appears
 *  nowhere in the flow being checked (and flips with declaration order). */
function bodyForDecl(
  models: readonly { readonly source?: SourceSpan; readonly body: Flow; readonly ownerPath?: QualifiedName }[] | undefined,
  source: SourceSpan | undefined,
  ownerPath?: QualifiedName
): Flow | undefined {
  if (!source) return undefined;
  const k = `${source.file}:${source.start.offset}`;
  const want = (ownerPath ?? []).join(".");
  const at = (m: { readonly source?: SourceSpan }): boolean =>
    !!m.source && `${m.source.file}:${m.source.start.offset}` === k;
  for (const m of models ?? []) {
    if (at(m) && (m.ownerPath ?? []).join(".") === want) return m.body;
  }
  // No owner-exact match (an unstamped decl, or an owner shape that doesn't
  // line up) — fall back to the source match, as before.
  for (const m of models ?? []) if (at(m)) return m.body;
  return undefined;
}

/**
 * Build the name → SubprocessDecl map visible to a given process per
 * owner-scope precedence (lowest priority first, later writes win):
 *   1. Free-standing top-level subprocesses (no ownerPath)
 *   2. Module-scoped subprocesses whose ownerPath is a prefix of the
 *      invoking process's ownerPath (shorter ancestors first, so closer
 *      ancestors overwrite)
 *   3. Subprocesses declared lexically inside the invoking process body
 */
function buildSubprocessLookup(
  proc: { readonly ownerPath?: QualifiedName; readonly subprocesses?: readonly SubprocessDecl[] },
  model: ResolvedModel
): Map<string, SubprocessDecl> {
  const out = new Map<string, SubprocessDecl>();
  const owner = proc.ownerPath ?? [];
  const isPrefix = (a: readonly string[], b: readonly string[]): boolean =>
    a.length <= b.length && a.every((seg, i) => seg === b[i]);
  const scoped: SubprocessDecl[] = [];
  for (const sp of model.subprocesses) {
    if (!sp.ownerPath || sp.ownerPath.length === 0) {
      out.set(sp.name, sp);
    } else if (isPrefix(sp.ownerPath, owner)) {
      scoped.push(sp);
    }
  }
  scoped.sort((a, b) => (a.ownerPath?.length ?? 0) - (b.ownerPath?.length ?? 0));
  for (const sp of scoped) out.set(sp.name, sp);
  if (proc.subprocesses) {
    for (const sp of proc.subprocesses) out.set(sp.name, sp);
  }
  return out;
}

/** Validate one process/subprocess body by reading the resolved {@link Flow} IR
 *  — the single traversal in {@link forEachNode}, never a re-walk of the AST.
 *  Every call HOP is caller/callee-checked; every `do` splice is target-checked.
 *  Owner-TODOs are emitted separately by {@link validateProcessOwners}. */
function validateFlow(
  body: Flow | undefined,
  index: SymbolIndex,
  subLookup: ReadonlyMap<string, SubprocessDecl>,
  procNames: ReadonlySet<string>,
  binds: ReadonlySet<string>,
  roleConflicts: ReadonlySet<string>,
  diagnostics: Diagnostic[],
  model: ResolvedModel,
  owner: readonly string[]
): void {
  forEachCallHop(body, (hop, node) => validateCallHop(hop, node, index, binds, roleConflicts, diagnostics));
  forEachNode(body, (n) => {
    if (n.kind === "splice") validateSpliceTarget(n, subLookup, procNames, diagnostics, model, owner);
  });
}

/** v0.9.0 §2.7: fields are header-zone only — one error per field written
 *  after the first action (parsed for recovery, rejected here). */
function validateHeaderFields(
  decl: { readonly lateFields?: readonly { readonly source?: SourceSpan; readonly path: readonly string[] }[] },
  diagnostics: Diagnostic[]
): void {
  for (const f of decl.lateFields ?? []) {
    diagnostics.push(
      makeDiag(
        "error",
        f.source,
        "PROCESS_FIELD_AFTER_ACTION",
        "validation.processFieldAfterAction",
        { field: f.path.join(".") }
      )
    );
  }
}

/** Process names reachable as v0.9.0 §2.5 `do` fallbacks FROM a declaration
 *  owned at `owner` — the same scope order as the graph/walker fallback:
 *  top-level processes plus module-scoped ones whose ownerPath prefixes the
 *  referrer's owner. Scope-blind acceptance would validate targets the
 *  emitter cannot resolve. */
function processNamesFor(model: ResolvedModel, owner: readonly string[]): ReadonlySet<string> {
  const names = new Set<string>();
  const isPrefix = (a: readonly string[], b: readonly string[]) =>
    a.length <= b.length && a.every((seg, i) => seg === b[i]);
  for (const p of model.processes ?? []) {
    if (!p.name) continue;
    if (!p.ownerPath || p.ownerPath.length === 0 || isPrefix(p.ownerPath, owner)) {
      names.add(p.name);
    }
  }
  return names;
}

/** Rank candidate qualified paths (item 3c): those under the referrer's owning
 *  module first, then the rest of the package; each group sorted, deduped, and
 *  the whole capped at 3 — deterministic across runs. */
function rankCandidates(paths: Iterable<string>, ownerPrefix: readonly string[]): string[] {
  const pfx = ownerPrefix.length > 0 ? ownerPrefix.join(".") + "." : "";
  const local: string[] = [];
  const rest: string[] = [];
  const seen = new Set<string>();
  for (const p of paths) {
    if (seen.has(p)) continue;
    seen.add(p);
    (pfx && p.startsWith(pfx) ? local : rest).push(p);
  }
  local.sort();
  rest.sort();
  return [...local, ...rest].slice(0, 3);
}

/** Did-you-mean for an unresolved interface callee (PROCESS_CALLEE_TODO): any
 *  DECLARED interface whose leaf matches the written leaf — the mis-owned
 *  `Orders.pay` when `pay` lives under `Payments.Api`. Candidates are the
 *  canonical FULL structural paths (one per interface — never the hoisted or
 *  §4.11-elided spellings, which all map to the same declaration). Own-module
 *  unique leaves no longer reach this hint at all: the elision RESOLVES them.
 *  Returned as `params.didYouMean` — the renderer, not this, makes it prose. */
function didYouMeanInterface(
  index: SymbolIndex,
  ownerPrefix: readonly string[],
  leaf: string,
  written: string
): string[] {
  if (!leaf) return [];
  const cands = new Set<string>();
  for (const list of index.ifaceCandidates.values()) {
    for (const c of list) {
      if (c.value === written) continue;
      if (c.path[c.path.length - 1] === leaf) cands.add(c.value);
    }
  }
  return rankCandidates(cands, ownerPrefix);
}

/** Did-you-mean for an unresolved `do` target (PROCESS_BAD_DO_TARGET): a
 *  same-named process or subprocess whose qualified path WOULD resolve from
 *  the referencing site — a suggested fix must not fail the same way when
 *  applied. Candidates mirror the resolution scope exactly: a module-scoped
 *  PROCESS or SUBPROCESS is globally FQN-addressable (spec §5.5), so any
 *  qualified path qualifies. */
function didYouMeanDoTarget(
  model: ResolvedModel,
  owner: readonly string[],
  shortName: string,
  written: string
): string[] {
  if (!shortName) return [];
  const cands: string[] = [];
  for (const p of model.processes ?? []) {
    if (p.name === shortName) {
      const fqn = processQualifiedName(p);
      if (fqn !== written) cands.push(fqn);
    }
  }
  for (const sp of model.subprocesses ?? []) {
    if (sp.name === shortName) {
      const fqn = subprocessQualifiedName(sp);
      if (fqn !== written) cands.push(fqn);
    }
  }
  return rankCandidates(cands, owner);
}

/** §4.11 cross-space `do`: flag a splice whose target is declared in a
 *  different space than the referring `do` and is NOT `export`ed. The
 *  attribution carve-out (graph.ts) hides the target's internals from the
 *  consumer regardless; this SURFACES the unsanctioned coupling so the architect
 *  either blesses it (`export`, top-level only) or removes it. Soft (warning),
 *  the same treatment as a cross-space call edge — a policy may escalate it. */
function crossSpaceDoDiag(
  n: SpliceNode,
  targetFile: string | undefined,
  targetExported: boolean,
  targetLabel: "subprocess" | "process",
  model: ResolvedModel,
  diagnostics: Diagnostic[]
): void {
  const spaces = model.spaces;
  if (!spaces || spaces.size === 0) return;
  const refFile = n.targetSource?.file ?? n.source?.file;
  const refSpace = refFile ? spaces.get(refFile)?.join(".") : undefined;
  const tgtSpace = targetFile ? spaces.get(targetFile)?.join(".") : undefined;
  if (refSpace === undefined || tgtSpace === undefined || refSpace === tgtSpace) return;
  if (targetExported) return; // `export` sanctions the cross-space entry point
  const qkey = n.target.join(".");
  diagnostics.push(
    makeDiag(
      "warning",
      n.targetSource ?? n.source,
      "CROSS_SPACE_COUPLING",
      "validation.crossSpaceCoupling",
      { qkey, targetLabel, tgtSpace, refSpace }
    )
  );
}

/** The process decl a `do` resolves to, matching the graph/walker precedence
 *  (graph.ts addProcessSpliceFallbacks): a QUALIFIED path names a module-scoped
 *  process by its FQN; a BARE name resolves to the nearest owner-prefix
 *  module-scoped process, else the top-level one. Used only to recover the
 *  target's declaring space + `export` flag for the cross-space check. */
function resolveDoProcessDecl(
  model: ResolvedModel,
  owner: readonly string[],
  last: string,
  qkey: string,
  qualified: boolean
): ProcessDecl | undefined {
  const procs = model.processes ?? [];
  if (qualified) return procs.find((p) => p.name && processQualifiedName(p) === qkey);
  const isPrefix = (a: readonly string[], b: readonly string[]): boolean =>
    a.length <= b.length && a.every((s, i) => s === b[i]);
  let best: ProcessDecl | undefined;
  let bestLen = -1;
  for (const p of procs) {
    if (p.name !== last) continue;
    const op = p.ownerPath ?? [];
    if (op.length === 0) {
      if (bestLen < 0) { best = p; bestLen = 0; } // top-level: weakest, only if nothing scoped yet
    } else if (isPrefix(op, owner) && op.length > bestLen) {
      best = p; bestLen = op.length; // nearest owner-prefix module-scoped wins
    }
  }
  return best;
}

function validateSpliceTarget(
  n: SpliceNode,
  subLookup: ReadonlyMap<string, SubprocessDecl>,
  procNames: ReadonlySet<string>,
  diagnostics: Diagnostic[],
  model: ResolvedModel,
  owner: readonly string[]
): void {
  // Lookup is by short name (last segment); qualified forms are accepted
  // when they match a fully-qualified key, but the primary lookup is by
  // last segment since `do Helper(...)` is the common form.
  const last = n.target[n.target.length - 1];
  const qkey = n.target.join(".");
  if (last && subLookup.has(last)) {
    // v0.9.0 §2.5: a same-name PROCESS is shadowed by the subprocess — say so.
    if (procNames.has(last)) {
      diagnostics.push(
        makeDiag(
          "warning",
          n.targetSource ?? n.source,
          "PROCESS_DO_SHADOWED",
          "validation.processDoShadowed",
          { qkey, last }
        )
      );
    }
    const sp = subLookup.get(last)!;
    crossSpaceDoDiag(n, sp.source?.file, sp.exported === true, "subprocess", model, diagnostics);
    return;
  }
  // Also accept dotted qualified targets — match if any subprocess in the
  // map has matching qualified name.
  for (const sp of subLookup.values()) {
    const spq = sp.ownerPath && sp.ownerPath.length > 0
      ? `${sp.ownerPath.join(".")}.${sp.name}`
      : sp.name;
    if (spq === qkey) {
      crossSpaceDoDiag(n, sp.source?.file, sp.exported === true, "subprocess", model, diagnostics);
      return;
    }
  }
  // A QUALIFIED target names the subprocess inline (spec §5.5 "Name
  // resolution"): `do <Module>.<sub>` addresses a module-nested subprocess by
  // its fully-qualified `<owner-path>.<name>` from anywhere, exactly like the
  // qualified-process arm below. Visibility is open within a space; a
  // cross-space `do` of a non-exported target is the soft flag emitted by
  // `crossSpaceDoDiag`, not an unresolved stub (§4.11).
  if (n.target.length >= 2) {
    const sp = model.subprocesses.find(
      (s) => s.ownerPath && s.ownerPath.length > 0 && `${s.ownerPath.join(".")}.${s.name}` === qkey
    );
    if (sp) {
      crossSpaceDoDiag(n, sp.source?.file, sp.exported === true, "subprocess", model, diagnostics);
      return;
    }
  }
  // v0.9.0 §2.5: `do <Process>` — consulted only after subprocesses miss.
  // A process declares no `on Caller` parameter, so a template-caller prefix
  // has no effect (warned, not an error).
  //
  // Two arms in the same subprocess-then-process order (PROCESS.md §7): the
  // bare short name resolves against the in-scope `procNames`; a QUALIFIED path
  // resolves against a module-scoped process's fully-qualified `<owner>.<name>`
  // (spec §5.5), which is globally addressable — so `do Notifications.Notify`
  // resolves even from a process outside `Notifications`.
  const qualifiedProcess =
    n.target.length >= 2 &&
    (model.processes ?? []).some((p) => p.name && processQualifiedName(p) === qkey);
  if ((last && procNames.has(last)) || qualifiedProcess) {
    if (n.templateCaller && n.templateCaller.form === "explicit") {
      diagnostics.push(
        makeDiag(
          "warning",
          n.targetSource ?? n.source,
          "PROCESS_DO_PROCESS_CALLER",
          "validation.processDoProcessCaller",
          { qkey, caller: n.templateCaller.resolved?.join(".") ?? "" }
        )
      );
    }
    // §4.11 cross-space `do` into a process: flag when the target's owning
    // space differs and it is not `export`ed. A module-scoped process is never
    // exportable, so a cross-space `do M.P` is always flagged (only a top-level
    // `export process` sanctions it).
    const target = resolveDoProcessDecl(model, owner, last ?? "", qkey, qualifiedProcess);
    if (target) {
      crossSpaceDoDiag(n, target.source?.file, target.exported === true, "process", model, diagnostics);
    }
    return;
  }
  // Forward reference: a `do X(...)` may target a subprocess or process not
  // defined yet (§4.13). The flow still draws; the missing target auto-stubs
  // as a SUBPROCESS TODO — an incompleteness to fill in, not a contradiction.
  // A same-named process/subprocess in another scope is the likeliest intent:
  // name its qualified path so a mis-scoped `do` is a one-hop fix (item 3b).
  const didYouMean = didYouMeanDoTarget(model, owner, last ?? "", qkey);
  diagnostics.push(
    makeDiag(
      "todo",
      n.targetSource ?? n.source,
      "PROCESS_BAD_DO_TARGET",
      "validation.processBadDoTarget",
      { qkey, didYouMean }
    )
  );
}

function validateCallHop(
  hop: CallHop,
  node: CallNode,
  index: SymbolIndex,
  binds: ReadonlySet<string>,
  roleConflicts: ReadonlySet<string>,
  diagnostics: Diagnostic[]
): void {
  const fromKey = hop.caller.join(".");
  // The written callee path: for an anonymous (module) callee the IR appends a
  // synthesized leaf to `interfacePath`, so the written form is `modulePath`;
  // for a real interface leaf it is `interfacePath`. Either way `toKey` is what
  // the author typed (matches the old `step.to.join('.')`).
  const toKey = (hop.callee.anonymous ? hop.callee.modulePath : hop.callee.interfacePath).join(".");
  // Point each diagnostic at the offending token — the caller name for caller
  // problems, the callee path for callee problems — falling back to the chain
  // span when the parser didn't record the token span.
  const fromSource = hop.callerSource ?? node.source;
  const toSource = hop.callee.text.source ?? node.source;

  // Caller. An anonymous head (`caller === []`, a `> Callee` step) is resolved
  // to the enclosing owner by the IR builder (§4.13.1); one that survives empty
  // has no enclosing module — a free-standing process — and is an error.
  //
  // A `\` snake head is the exception (§4.13.4 rule 3): a tail is never
  // inherited from a container, so a dangling one is a DRAFT-FLOOR state, not a
  // malformed process — it parses and renders. It gets a `todo` caller (so the
  // zero-TODO merge gate still holds it) plus a warning naming the cause, and
  // never the anon-head error, which would refuse to render it at all.
  if (hop.caller.length === 0 && node.owner.form === "snake") {
    diagnostics.push(
      makeDiag("todo", fromSource, "SNAKE_NO_TAIL", "validation.snakeNoTail")
    );
  } else if (hop.caller.length === 0) {
    diagnostics.push(
      makeDiag(
        "error",
        fromSource,
        "PROCESS_ANON_HEAD_AT_ROOT",
        "validation.processAnonHeadAtRoot"
      )
    );
  } else if (!index.modules.has(fromKey) && !binds.has(fromKey)) {
    // Otherwise classify (§10.2): module/actor/bound → ok; interface leaf → role
    // conflict (error); nothing → not defined yet → TODO (auto-stubs a module).
    if (index.interfaces.has(fromKey)) {
      diagnostics.push(
        makeDiag(
          "error",
          fromSource,
          "PROCESS_BAD_CALLER",
          "validation.processBadCaller",
          { caller: fromKey }
        )
      );
    } else if (roleConflicts.has(fromKey)) {
      diagnostics.push(
        makeDiag(
          "error",
          fromSource,
          "PROCESS_ROLE_CONFLICT",
          "validation.processRoleConflictCaller",
          { name: fromKey }
        )
      );
    } else {
      diagnostics.push(
        makeDiag(
          "todo",
          fromSource,
          "PROCESS_CALLER_TODO",
          "validation.processCallerTodo",
          { caller: fromKey }
        )
      );
    }
  }

  // v0.8.2 hoisting: a caller reference (of ANY length) that hoists to more than
  // one module is ambiguous — flag it (candidates listed); qualify or use #id.
  // A bound name (`x = …` then `x > …`) is the innermost scope and shadows the
  // modules (§4.11 nearest-first), so it never triggers module ambiguity.
  const callerSpace = index.spaces && fromSource?.file ? index.spaces.get(fromSource.file) : undefined;
  const calleeSpace = index.spaces && toSource?.file ? index.spaces.get(toSource.file) : undefined;
  if (hop.caller.length >= 1 && !binds.has(fromKey)) {
    const cands = ambiguousModuleCandidates(index, fromKey, callerSpace);
    if (cands) {
      diagnostics.push(
        makeDiag(
          "warning",
          fromSource,
          "AMBIGUOUS_MODULE_REF",
          "validation.ambiguousModuleRefCaller",
          { caller: fromKey, candidates: [...cands].sort().join(", ") }
        )
      );
    }
  }

  // Callee, read from the IR's resolved verdict (`ResolvedCallee`, computed once
  // by the builder via the shared `classifyCallee`, §10.3):
  //   - role conflict (same name used as both module and interface) → error
  //   - anonymous (module callee / trailing dot / single bare segment) → a
  //     synthesized handler interface stands in → TODO (unspecified)
  //   - declared interface leaf → ok
  //   - undeclared interface leaf → not defined yet → TODO (auto-stubs it)
  if (roleConflicts.has(toKey)) {
    diagnostics.push(
      makeDiag(
        "error",
        toSource,
        "PROCESS_ROLE_CONFLICT",
        "validation.processRoleConflictCallee",
        { name: toKey }
      )
    );
  } else if (hop.callee.anonymous) {
    diagnostics.push(
      makeDiag(
        "todo",
        toSource,
        "PROCESS_CALLEE_UNSPECIFIED",
        "validation.processCalleeUnspecified",
        { callee: toKey }
      )
    );
  } else if (!index.interfaces.has(hop.callee.interfacePath.join("."))) {
    const written = hop.callee.interfacePath.join(".");
    const leaf = hop.callee.interfacePath[hop.callee.interfacePath.length - 1] ?? "";
    const didYouMean = didYouMeanInterface(index, hop.callee.modulePath, leaf, written);
    diagnostics.push(
      makeDiag(
        "todo",
        toSource,
        "PROCESS_CALLEE_TODO",
        "validation.processCalleeTodo",
        { callee: toKey, didYouMean }
      )
    );
  }

  // v0.8.2 hoisting: a callee module reference (of ANY length) that hoists to more
  // than one module is ambiguous.
  let calleeModuleAmbiguous = false;
  if (hop.callee.modulePath.length >= 1) {
    const name = hop.callee.modulePath.join(".");
    const cands = ambiguousModuleCandidates(index, name, calleeSpace);
    if (cands) {
      calleeModuleAmbiguous = true;
      diagnostics.push(
        makeDiag(
          "warning",
          toSource,
          "AMBIGUOUS_MODULE_REF",
          "validation.ambiguousModuleRefCallee",
          { name, candidates: [...cands].sort().join(", ") }
        )
      );
    }
  }

  // §4.11 surface elision: an interface spelling answered by 2+ declared
  // interfaces (two same-leaf surface interfaces of one module, or a cross-rule
  // suffix/elision collision) with no exact winner is ambiguous — flagged, not
  // stubbed and not a TODO (the leaves EXIST; the reference just underspecifies
  // which). Suppressed when the module prefix itself is ambiguous — that
  // diagnostic already names the root cause, and qualifying the module usually
  // resolves the leaf too.
  if (!hop.callee.anonymous && !calleeModuleAmbiguous) {
    const written = hop.callee.interfacePath.join(".");
    const cands = ambiguousIfaceCandidates(index, written, calleeSpace);
    if (cands) {
      diagnostics.push(
        makeDiag(
          "warning",
          toSource,
          "AMBIGUOUS_INTERFACE_LEAF",
          "validation.ambiguousInterfaceLeaf",
          { written, candidates: [...cands].sort().join(", ") }
        )
      );
    }
  }
}

// =============================================================================
// Walkers
// =============================================================================

function walkModules(
  modules: readonly ResolvedModule[],
  visit: (m: ResolvedModule) => void
): void {
  for (const m of modules) {
    visit(m);
    walkModules(m.modules, visit);
  }
}

function walkSurfaces(
  modules: readonly ResolvedModule[],
  visit: (f: ResolvedSurface) => void
): void {
  function walkSurfaceTree(surfaces: readonly ResolvedSurface[]): void {
    for (const f of surfaces) {
      visit(f);
      walkSurfaceTree(f.surfaces);
    }
  }
  for (const m of modules) {
    walkSurfaceTree(m.surfaces);
    walkModules(m.modules, (sub) => walkSurfaceTree(sub.surfaces));
  }
}

function walkInterfaces(
  modules: readonly ResolvedModule[],
  visit: (i: ResolvedInterface) => void
): void {
  function walkInSurfaces(surfaces: readonly ResolvedSurface[]): void {
    for (const f of surfaces) {
      for (const i of f.interfaces) visit(i);
      walkInSurfaces(f.surfaces);
    }
  }
  for (const m of modules) {
    for (const i of m.interfaces) visit(i);
    walkInSurfaces(m.surfaces);
    walkModules(m.modules, (sub) => {
      for (const i of sub.interfaces) visit(i);
      walkInSurfaces(sub.surfaces);
    });
  }
}
